import type React from 'react';
import classes from './home-page.module.scss';
import { AssignmentList } from './components/AssignmentList';
import { formatWeekRange } from '../../utils/date.utils';
import { useHomePageController } from './useHomePageController';

export const HomePage: React.FC = () => {
  const {
    navigate,
    user,
    dashboard,
    announcement,
    announcementDraft,
    setAnnouncementDraft,
    isEditingAnnouncement,
    setIsEditingAnnouncement,
    isSavingAnnouncement,
    isLoading,
    error,
    loadDashboard,
    onSaveAnnouncement,
  } = useHomePageController();

  if (isLoading) {
    return (
      <section className="contentCard" dir="rtl">
        <h1 className="homeTitle">דף הבית</h1>
        <p className="homeHint">טוען סידור עבודה...</p>
      </section>
    );
  }

  if (error || !dashboard) {
    return (
      <section className="contentCard" dir="rtl">
        <h1 className="homeTitle">דף הבית</h1>
        <p className="errorText">{error ?? 'לא ניתן לטעון נתונים'}</p>
        <button
          className="button retryButton"
          type="button"
          onClick={() => {
            void loadDashboard();
          }}
        >
          נסה שוב
        </button>
      </section>
    );
  }

  const hasCurrentWeekAssignments = dashboard.currentWeek.assignments.length > 0;

  const hasNextWeekAssignments = dashboard.nextWeek.assignments.length > 0;

  const isManager = user?.role === 'manager';

  const renderNextWeekBody = () => {
    if (!hasNextWeekAssignments) {
      return <p className="emptyStateText">לא שובצת למשמרות בשבוע הבא.</p>;
    }

    return <AssignmentList assignments={dashboard.nextWeek.assignments} />;
  };

  return (
    <section className={classes.homeDashboard} dir="rtl" aria-label="דף הבית">
      <div className={classes.homeMainColumn}>
        <article className={`contentCard ${classes.homePanel} ${classes.weekCard}`}>
          <div className={classes.weekCardHeader}>
            <div>
              <h1 className="homeTitle">סידור לשבוע הנוכחי</h1>
              <p className="homeHint">
                {formatWeekRange(dashboard.currentWeek.fromDate, dashboard.currentWeek.toDate)}
              </p>
              <p className={classes.welcomeText}>שלום {user?.employee?.fullName ?? user?.username}</p>
            </div>
            <button className="navArrowButton" type="button" onClick={() => navigate('/schedule')}>
              לסידור המלא ←
            </button>
          </div>
          {hasCurrentWeekAssignments ? (
            <AssignmentList assignments={dashboard.currentWeek.assignments} />
          ) : (
            <p className="emptyStateText">לא שובצת למשמרות בשבוע הנוכחי.</p>
          )}
        </article>

        <article className={`contentCard ${classes.homePanel} ${classes.weekCard}`}>
          <div className={classes.weekCardHeader}>
            <div>
              <h2 className="homeTitle">סידור לשבוע הבא</h2>
              <p className="homeHint">
                {formatWeekRange(dashboard.nextWeek.fromDate, dashboard.nextWeek.toDate)}
              </p>
            </div>
            <button className="navArrowButton" type="button" onClick={() => navigate('/schedule')}>
              לסידור המלא ←
            </button>
          </div>
          {renderNextWeekBody()}
        </article>
      </div>

      <aside className={classes.homeSidebar} aria-label="מידע נוסף">
        <section
          className={`contentCard ${classes.homePanel} ${classes.homeInfoPanel}`}
          aria-label="הודעת מנהל"
        >
          <div className={classes.homePanelHeader}>
            <h2 className={classes.homeSectionTitle}>הודעת מנהל</h2>
            {isManager && !isEditingAnnouncement && (
              <button
                className={classes.homeAnnouncementEditButton}
                type="button"
                onClick={() => setIsEditingAnnouncement(true)}
              >
                עריכה
              </button>
            )}
          </div>
          {isEditingAnnouncement ? (
            <div className={classes.homeAnnouncementEditor}>
              <label className="label" htmlFor="manager-announcement">
                תוכן ההודעה
              </label>
              <textarea
                id="manager-announcement"
                className={classes.homeAnnouncementTextarea}
                value={announcementDraft}
                maxLength={2000}
                onChange={(event) => setAnnouncementDraft(event.target.value)}
              />
              <div className={classes.homeAnnouncementActions}>
                <button
                  className={classes.homeAnnouncementSecondaryButton}
                  type="button"
                  disabled={isSavingAnnouncement}
                  onClick={() => {
                    setAnnouncementDraft(announcement);
                    setIsEditingAnnouncement(false);
                  }}
                >
                  ביטול
                </button>
                <button
                  className={classes.homeAnnouncementSaveButton}
                  type="button"
                  disabled={isSavingAnnouncement}
                  onClick={() => {
                    void onSaveAnnouncement();
                  }}
                >
                  {isSavingAnnouncement ? 'שומר...' : 'שמירה'}
                </button>
              </div>
            </div>
          ) : announcement ? (
            <p className={classes.homeAnnouncementText}>{announcement}</p>
          ) : (
            <p className={classes.homePanelEmpty}>אין הודעת מנהל כרגע.</p>
          )}
        </section>

        <section className={`contentCard ${classes.homePanel} ${classes.homeInfoPanel}`}>
          <div className={classes.homePanelHeader}>
            <h2 className={classes.homeSectionTitle}>רשימת קבצים</h2>
          </div>
          <p className={classes.homePanelEmpty}>אין קבצים להצגה.</p>
        </section>
      </aside>
    </section>
  );
};
